import { useLocalObservable } from 'mobx-react-lite';
import { RefObject, useEffect, useRef } from 'react';

import {
  HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION,
  HIGHLIGHT_HOLD_ANIMATION_DURATION,
} from '../../Message.constants';

interface IUseQuotedSelectionHighlightPresenterProps {
  highlightedText?: string;
  originalText?: string;
  isView?: boolean;
}

interface IQuotedSelectionHighlightPresenter {
  showHighlight: boolean;
  highlightedText: string | null;
  originalText: string | null;
  setShowHighlight(showHighlight: boolean): void;
  setHighlightedText(highlightedText: string | null): void;
  setOriginalText(originalText: string | null): void;
  unsetHighlightedText(): void;
}

interface IUseQuotedSelectionHighlightPresenter {
  presenter: IQuotedSelectionHighlightPresenter;
  rootRef: RefObject<HTMLDivElement>;
  highlightRef: RefObject<HTMLDivElement>;
}

export function useQuotedSelectionHighlightPresenter({
  highlightedText,
  originalText,
  isView,
}: IUseQuotedSelectionHighlightPresenterProps): IUseQuotedSelectionHighlightPresenter {
  const rootRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const presenter = useLocalObservable<IQuotedSelectionHighlightPresenter>(() => ({
    highlightedText: null,
    originalText: null,
    showHighlight: false,

    setShowHighlight(showHighlight: boolean): void {
      presenter.showHighlight = showHighlight;
    },
    setHighlightedText(highlightedText: string | null): void {
      presenter.highlightedText = highlightedText;
    },
    setOriginalText(originalText: string | null): void {
      presenter.originalText = originalText;
    },
    unsetHighlightedText(): void {
      presenter.highlightedText = null;
      presenter.setShowHighlight(false);
    },
  }));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null;
    if (!presenter.highlightedText) {
      return;
    }

    presenter.setShowHighlight(true);

    if (isView) {
      timer = setTimeout(() => {
        presenter.setShowHighlight(false);
      }, HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION + HIGHLIGHT_HOLD_ANIMATION_DURATION);
    }

    return () => {
      if (timer) {
        presenter.unsetHighlightedText();
        clearTimeout(timer);
      }
    };
  }, [presenter.highlightedText, isView]);

  useEffect(() => {
    if (highlightedText) {
      presenter.setHighlightedText(highlightedText);
    }
  }, [highlightedText]);

  useEffect(() => {
    presenter.setOriginalText(originalText || null);
  }, [originalText]);

  return { presenter, rootRef, highlightRef };
}
